import { motion } from "framer-motion";
import { Smartphone, BellRing, ClipboardList, Coins, ParkingCircle, ArrowUpRight } from "lucide-react";
import { Reveal, SectionLabel } from "./Reveal";
import { EASE, scrollToId } from "./data";

const LOG = [
  { plate: "NC · KTR-4821", spot: "Fire lane · Bldg C", time: "11:42 PM" },
  { plate: "NC · PLM-0937", spot: "Reserved #14", time: "9:18 PM" },
  { plate: "SC · 3GH-552", spot: "Resident only · Lot B", time: "6:05 PM" },
];

const PhoneMock = () => (
  <motion.div
    initial={{ opacity: 0, y: 60, rotate: -4 }}
    whileInView={{ opacity: 1, y: 0, rotate: -4 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 1.1, ease: EASE }}
    whileHover={{ rotate: 0, y: -6 }}
    aria-hidden="true"
    className="relative mx-auto w-[250px] rounded-[38px] border border-white/15 bg-[#050506] p-3 shadow-[0_40px_80px_-20px_rgba(116,166,54,0.35)]"
  >
    <div className="mx-auto mb-3 h-5 w-24 rounded-full bg-white/10" />
    <div className="rounded-[26px] bg-[var(--ink-2)] p-4">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Your property</p>
      <p className="mt-1 font-display text-2xl font-extrabold uppercase leading-none">Activity</p>
      <div className="mt-4 space-y-2">
        {LOG.map((l, i) => (
          <motion.div key={l.plate} initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: 0.5 + i * 0.15, ease: EASE }}
            className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
            <div className="flex items-center justify-between">
              <span className="font-mono text-[11px] text-[var(--bone)]">{l.plate}</span>
              <span className="font-mono text-[10px] text-zinc-400">{l.time}</span>
            </div>
            <p className="mt-1 text-[12px] text-zinc-300">{l.spot}</p>
            <span className="mt-2 inline-block rounded-full bg-[var(--signal)]/15 px-2 py-0.5 font-mono text-[10px] uppercase text-[var(--signal)]">Removed</span>
          </motion.div>
        ))}
      </div>
    </div>
  </motion.div>
);

const Point = ({ icon: Icon, t, d }) => (
  <li className="flex gap-4">
    <Icon className="mt-1 h-5 w-5 shrink-0 text-[var(--signal)]" strokeWidth={1.75} />
    <div>
      <p className="font-display text-xl font-extrabold uppercase leading-none">{t}</p>
      <p className="mt-1 text-sm leading-relaxed text-zinc-300">{d}</p>
    </div>
  </li>
);

const AppCard = () => (
  <Reveal className="lg:col-span-7">
    <article data-testid="owner-app-card" className="grid h-full gap-10 overflow-hidden border border-[var(--line)] bg-[var(--ink-2)] p-6 sm:p-10 md:grid-cols-2 md:items-center">
      <div>
        <Smartphone className="h-7 w-7 text-[var(--signal)]" strokeWidth={1.5} />
        <h3 className="mt-6 font-display text-4xl font-black uppercase leading-[0.9] sm:text-5xl">The property<br />manager app</h3>
        <p className="mt-4 text-base leading-relaxed text-zinc-300">Our state-of-the-art app keeps property managers in the loop on everything happening at their property.</p>
        <ul className="mt-8 space-y-5">
          <Point icon={BellRing} t="Always in the loop" d="See enforcement activity on your property without making a call." />
          <Point icon={ClipboardList} t="Every tow on record" d="Plate, time, location and reason for each removal." />
        </ul>
      </div>
      <PhoneMock />
    </article>
  </Reveal>
);

const ParkCard = () => (
  <Reveal delay={0.1} className="lg:col-span-5">
    <article data-testid="pay2park-card" className="relative flex h-full min-h-[460px] flex-col justify-between overflow-hidden bg-[var(--signal)] p-6 text-[var(--ink)] sm:p-10">
      <ParkingCircle aria-hidden="true" className="pointer-events-none absolute -right-16 -top-16 h-72 w-72 text-black/10" strokeWidth={1} />
      <div className="relative flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em]">
        <Coins className="h-5 w-5" /> New revenue
      </div>
      <div className="relative">
        <h3 className="font-display text-6xl font-black uppercase leading-[0.85] sm:text-7xl">Pay 2<br />Park</h3>
        <p className="mt-5 max-w-sm text-base leading-relaxed text-black/80">Turn open spaces into income. Pay 2 Park lets drivers pay to park on your lot, bringing more revenue to your property, while Warrior keeps the rules enforced.</p>
        <button onClick={() => scrollToId("quote")} data-testid="pay2park-cta" className="group mt-8 flex min-h-[52px] items-center gap-3 bg-[var(--ink)] px-6 font-display text-xl font-extrabold uppercase text-[var(--bone)] transition-transform duration-300 hover:-translate-y-1">
          Ask about Pay 2 Park <ArrowUpRight className="h-5 w-5 text-[var(--signal)] transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
        </button>
      </div>
    </article>
  </Reveal>
);

export const OwnerTools = () => (
  <section id="tools" data-testid="tools-section" className="mx-auto max-w-[1400px] px-5 py-24 sm:px-8 lg:py-32">
    <Reveal className="mb-14 grid gap-6 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <SectionLabel index="03">Owner tools</SectionLabel>
        <h2 className="font-display text-5xl font-black uppercase leading-[0.9] sm:text-6xl lg:text-7xl">More than<br />a tow truck.</h2>
      </div>
      <p className="self-end text-base leading-relaxed text-zinc-300 lg:col-span-5">Technology that keeps you informed, and a parking program that helps your lot earn more.</p>
    </Reveal>
    <div className="grid gap-4 lg:grid-cols-12">
      <AppCard />
      <ParkCard />
    </div>
  </section>
);
